import axios from "axios";
import { store } from "../store";
import { clearUser } from "../components/auth/store/userSlice";
import { fetchCsrfToken, clearCsrfToken } from "../components/auth/services/CSRF_Service.js";

let isRefreshing = false;
let failedQueue = [];

// ✅ Always pull from in-memory cache only — no localStorage
async function getCsrfToken() {
  return await fetchCsrfToken();
}

const processQueue = (error = null) => {
  failedQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve();
  });
  failedQueue = [];
};

// ---- Refresh API instance ----
const refreshApi = axios.create({
  baseURL: process.env.REACT_APP_AUTH_BASE_URL,
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

// ---- Refresh call (COOKIE BASED) ----
// ✅ Now includes CSRF token — required for POST on candidateChain
async function refreshAccessToken() {
  const token = await getCsrfToken();
  await refreshApi.post(
    "/candidate-auth/refresh-token",
    null,
    {
      headers: {
        "X-Client": "candidate",
        "X-XSRF-TOKEN": token,
      },
      withCredentials: true,
    }
  );
}

// ---- Apply interceptors ----
export function applyInterceptors(axiosInstance) {
  // ---------------- REQUEST ----------------
  axiosInstance.interceptors.request.use(
    async (config) => {
      const method = config.method?.toUpperCase();
      if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const token = await getCsrfToken();
        config.headers["X-XSRF-TOKEN"] = token;
      }
      return config;
    }
  );

  // ---------------- RESPONSE ----------------
  axiosInstance.interceptors.response.use(
    (response) => response.data,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;


     if ((status !== 401 && status !== 403) || originalRequest._retry) {
        throw error;
      }
     

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axiosInstance(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        await refreshAccessToken();
        processQueue();
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearCsrfToken(); // ✅ Invalidate stale CSRF token on session loss
        store.dispatch(clearUser());
        window.location.href = "/login";
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }
  );
}